import { Injectable } from '@angular/core';

import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { values } from 'lodash-es';

import { BetslipPick } from '../../../../core/picks/betslip-pick';
import { IFailedSlipResult } from '../../../betplacement/models';
import { CutOffDateReached } from '../../errors/result/cut-off-date-reached';
import { OddsChanged } from '../../errors/result/odds-changed';
import { OpenDateNotReached } from '../../errors/result/open-date-not-reached';
import { PickInvisible } from '../../errors/result/pick-invisible';
import { PickStatusChanged } from '../../errors/result/pick-status-changed';
import { ResultError } from '../../errors/result/result-error';
import BetslipErrorChangedOptionStatusUpdater from './pick-updaters/betslip-error-changed-option-status-updater';
import BetslipErrorCutoffDateReachedUpdater from './pick-updaters/betslip-error-cutoff-date-reached-updater';
import BetslipErrorOddsChangedUpdater from './pick-updaters/betslip-error-odds-changed-updater';
import BetslipErrorOpenDateNotReachedUpdater from './pick-updaters/betslip-error-open-date-not-reached-updater';
import BetslipErrorPickInvisibleUpdater from './pick-updaters/betslip-error-pick-invisible-updater';
import { IPickUpdater } from './pick-updaters/pick-updater';

@Injectable({ providedIn: 'root' })
export class BetslipErrorPickUpdaterService {
    private readonly pickUpdaters: Map<Function, IPickUpdater> = new Map<Function, IPickUpdater>();
    private readonly pickUpdaterKeys: Array<Function>;

    constructor() {
        this.pickUpdaters
            .set(PickInvisible, new BetslipErrorPickInvisibleUpdater())
            .set(OddsChanged, new BetslipErrorOddsChangedUpdater())
            .set(CutOffDateReached, new BetslipErrorCutoffDateReachedUpdater())
            .set(OpenDateNotReached, new BetslipErrorOpenDateNotReachedUpdater())
            .set(PickStatusChanged, new BetslipErrorChangedOptionStatusUpdater());

        this.pickUpdaterKeys = Array.from(this.pickUpdaters.keys());
    }

    getUpdatedPicks(result: IFailedSlipResult): BetslipPick[] {
        const updatedPicks: BetslipPick[] = [];
        for (const pick of values(result.picks)) {
            let isPickUpdated = false;
            const copy = pick.clientPick.copy();
            for (const error of pick.errors) {
                const updater = this.getPickUpdater(error);
                if (!updater) {
                    continue;
                }
                updater.updatePick(copy, error);
                isPickUpdated = true;
            }
            if (isPickUpdated) {
                updatedPicks.push(copy);
            }
        }

        return updatedPicks;
    }

    private getPickUpdater(error: ResultError): Nullable<IPickUpdater> {
        const updaterKey = this.pickUpdaterKeys.find((key) => error instanceof key);
        if (!updaterKey) {
            return null;
        }

        const pickUpdater = this.pickUpdaters.get(updaterKey);

        return pickUpdater ? pickUpdater : null;
    }
}
